// One vocabulary for `tenders.listing_type` — which of a portal's own lists a
// tender came from. The scrapers store the portal's own word for it, so the raw
// values are not uniform across portals and are not fit to show as they are:
//   rites        live | under_evaluation
//   bihar        latest | upcoming
//   wapcos       awarded | tender | eoi
//   upmrc        active | awarded | archived
//   nagpurmetro  active | progress | awarded | urban
//   cpwd         live | opened
//   thanemetro   active | progress | awarded | eoi
//
// What matters to someone searching for work is narrower than that list: can I
// still bid on this? So each value maps to a short label and one of three
// stages. Anything unknown (a portal adds a list, a new portal is integrated)
// falls through to a title-cased version of the raw value rather than being
// hidden, so a new value is visible rather than silently dropped.

// open     — bidding is or will be possible
// decided  — bidding is over: evaluation, award, work underway
// neutral  — the portal's own grouping, which says nothing about bidding
export const LISTING_TYPE = {
  live:             { label: "Live",             stage: "open" },
  active:           { label: "Active",           stage: "open" },
  latest:           { label: "Latest",           stage: "open" },
  tender:           { label: "Tender",           stage: "open" },
  upcoming:         { label: "Upcoming",         stage: "open" },
  eoi:              { label: "EOI",              stage: "open" },

  opened:           { label: "Bids Opened",      stage: "decided" },
  under_evaluation: { label: "Under Evaluation", stage: "decided" },
  awarded:          { label: "Awarded",          stage: "decided" },
  progress:         { label: "In Progress",      stage: "decided" },
  archived:         { label: "Archived",         stage: "decided" },

  urban:            { label: "Urban Development", stage: "neutral" },
};

function titleCase(value) {
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// The label to show for a stored value, or "" when there is none to show.
export function listingTypeLabel(value) {
  if (!value) return "";
  return (LISTING_TYPE[value] && LISTING_TYPE[value].label) || titleCase(value);
}

// StatusBadge type for a stored value. "decided" reads as neutral grey rather
// than danger: an awarded contract is not an error, it is simply not biddable.
export function listingTypeBadge(value) {
  const stage = (LISTING_TYPE[value] || {}).stage;
  if (stage === "open") return "success";
  return "neutral";
}

// True when the tender can no longer be bid on, whatever its closing date says.
// An awarded contract carries no deadline, so the date alone would leave it
// looking permanently open.
export function isDecided(value) {
  return (LISTING_TYPE[value] || {}).stage === "decided";
}

// Facet rows ({value, count}) ordered for a dropdown: biddable first, then the
// rest, each group by count. The API orders by count alone, which buries "Live"
// under thousands of awarded contracts.
export function orderFacets(rows) {
  const weight = (v) => {
    const stage = (LISTING_TYPE[v] || {}).stage;
    return stage === "open" ? 0 : stage === "neutral" ? 1 : 2;
  };
  return [...(rows || [])].sort(
    (a, b) => weight(a.value) - weight(b.value) || b.count - a.count
  );
}
